import { ZIO } from "@mxt/zio"
import { useLoading } from "@mxt/zio-react"
import { ContractMapping, PremiumStatusMapping, PulseOpsFormat, Select, SelectOption, assets, useTranslator } from "@pulseops/common"
import { IBGeneralTable, IBService, IBStyles, PolicySearchItem } from "@pulseops/inbound"
import { OBGeneralService, OBPolicyData, OBSectionCol, OBSectionContent, OBSectionRow, OBTitle } from "@pulseops/outbound"
import { addDays } from "date-fns"
import { pipe } from "fp-ts/lib/function"
import _ from "lodash"
import moment from "moment"
import React from "react"
import { useTranslation } from "react-i18next"
import { View, Text } from "react-native"

interface Props {
  policyNumber: string
  clientNumber: string
  caseId: string
  dataPolicy: OBPolicyData | null
  setDataPolicy: (data: OBPolicyData | null) => void
}

export const OBOthersTab = ({ policyNumber, clientNumber, caseId, dataPolicy, setDataPolicy }: Props) => {
  const { t,i18n } = useTranslation()
  const ct = useTranslator('contract').t
  const ps = useTranslator('premiumStatus').t
  const [loading, bindLoading] = useLoading(false)
  const [dataFilter, setDataFilter] = React.useState<PolicySearchItem[]>([])
  const [page, setPage] = React.useState<number>(0)
  const [pageSize, setPageSize] = React.useState<number>(10)
  const [taskCount, setTaskCount] = React.useState<number>(0)
  const [loadingFirst, setLoadingFirst] = React.useState<boolean>(false)
  
  const listStatusGroup = [
    {
      label: t('Outbound:OBSpecialInfo:All'),
      value: '1'
    },
    {
      label: t('Outbound:OBSpecialInfo:Proposal'),
      value: '2'
    },
    {
      label: t('Outbound:OBSpecialInfo:Inforce'),
      value: '3'
    },
    {
      label: t('Outbound:OBSpecialInfo:Lapsed'),
      value: '4'
    },
    {
      label: t('Outbound:OBSpecialInfo:Maturity'),
      value: '5'
    },
    {
      label: t('Outbound:OBSpecialInfo:DeathClaimTPD'),
      value: '6'
    },
    {
      label: t('Outbound:OBSpecialInfo:Others'),
      value: '7'
    }
  ]

  const listRole = [
    {
      label: t('Inbound:All'),
      value: ''
    },
    {
      label: t('Inbound:PO'),
      value: 'OW'
    },
    {
      label: t('Inbound:LA'),
      value: 'LF'
    },
    {
      label: t('Inbound:BEN'),
      value: 'BN'
    }
  ]
  const [itemsFilter, setItemsFilter] = React.useState<{statusGroup: SelectOption, role: SelectOption}>({statusGroup: listStatusGroup[0], role: listRole[0]})

  React.useEffect(() => {
    setDataPolicy(null)
    if(policyNumber && caseId) {
      pipe(
        OBGeneralService.getPolicyInfo(policyNumber, caseId),
        ZIO.map((data) => {
          setDataPolicy(data)
        }),
        ZIO.unsafeRun({})
      )
    }  
  }, [policyNumber, caseId])

  React.useEffect(() => {
    if (!loadingFirst) {
      setLoadingFirst(true)
      handlePolicySearch(page, pageSize)
    }
  }, [clientNumber, policyNumber])

  React.useEffect(() => {
    if (loadingFirst) {
      handlePolicySearch(page, pageSize)
    }
  }, [page, pageSize, itemsFilter])

  const dataTable = [
    {
      label: t('common:PolicyNumber'),
      field: 'policyNumber'
    },
    {
      label: t('Inbound:ProposalNum'),
      field: 'proposalNo',
      render: (value: string) => <Text>{value}</Text>
    },
    {
      label: t('Inbound:Product'),
      field: i18n.language === 'en' ? 'mainProductNameEn' : 'mainProductNameVi'
    },
    {
      label: t('Inbound:PolicyStatus'),
      field: 'policyStatus',
      render: (value: string) => {
        return ContractMapping.get(ct)(value)
      }
    },
    {
      label: t('Inbound:PremiumStatus'),
      field: 'premiumStatus',
      render: (value: string) => {
        return PremiumStatusMapping.get(ps)(value)
      }
    },
    {
      label: 'PO',
      field: 'isPO',
      render: (value: string) => {
        return value ? <assets.IBCheckRole /> : <></>
      }
    },
    {
      label: 'LA',
      field: 'isLA',
      render: (value: string) => {
        return value ? <assets.IBCheckRole /> : <></>
      }
    },
    {
      label: 'BEN',
      field: 'isBEN',
      render: (value: string) => {
        return value ? <assets.IBCheckRole /> : <></>
      }
    },
    {
      label: t('Inbound:OwnerName'),
      field: 'poName',
    },
    {
      label: t('Inbound:MainLifeAssured'),
      field: 'mainLAName' 
    },
    {
      label: t('Inbound:PaidToDate'),
      field: 'paidToDateBasic',
      format: 'date'
    }
    // {
    //   label: t('APL'),
    //   field: 'aplAmount'
    // }
  ]

  const getPolicyStatusFilter = (key: string) => {
    switch (key) {
      case '1':
        return [
          'PS',
          'IF',
          'PU',
          'LA',
          'FZ',
          'MA',
          'DH',
          'TD',
          'RD',
          'DE',
          'PO',
          'WD',
          'DC',
          'CF',
          'SU',
          'TR',
          'EX',
          'AP',
          'CP',
          'HP',
          'MP',
          'PW',
          'TM',
          'UW',
          'VO',
          'VR'
        ]
      case '2':
        return ['PS']
      case '3':
        return ['IF', 'PU']
      case '4':
        return ['LA', 'FZ']
      case '5':
        return ['MA']
      case '7':
        return ['DH', 'TD', 'RD', 'DE']
      case '8':
        return ['PO', 'WD', 'DC', 'CF', 'SU', 'TR', 'EX', 'AP', 'CP', 'HP', 'MP', 'PW', 'TM', 'UW', 'VO', 'VR']
      default:
        return []
    }
  }

  const checkRulePaidToDate = (
    policyStatus: string,
    premCessDate: string,
    nextPaidPremium: number,
    paidToDate: string
  ) => {
    const checkDate = moment(premCessDate, 'DD/MM/YYYY').diff(moment(paidToDate, 'DD/MM/YYYY'))
    if (policyStatus === 'IF' && checkDate > 0 && nextPaidPremium > 0) {
      return true
    }
    if (policyStatus === 'LA' && checkDate) {
      return true
    }
    return false
  }

  const handlePolicySearch = (page: number, pageSize: number, searchByFilter?: boolean) => {
    if (policyNumber && clientNumber) {
      pipe(
        IBService.searchRelatedPolicy({
          pageIndex: page,
          itemsPerPage: pageSize,
          orders: ['policyNo'],
          sort: 'DESC',
          role: itemsFilter.role?.value ?? '',
          lstPolicyStatusFilter: itemsFilter.statusGroup ? getPolicyStatusFilter(itemsFilter.statusGroup?.value ) : [],
          clientNumber: clientNumber,
          policyNo: '',
        }),
        ZIO.map((res) => {
          setDataFilter(
            res.records.map((item) => ({
              ...item,
              isPO: item.roles?.includes('OW'),
              isLA: item.roles?.includes('LF'),
              isBEN: item.roles?.includes('BN'),
              mainLAName: item.mainLifeAssuredName?.name,
              poName: item.policyOwnerName?.name,
              mainProductNameVi: item.mainProduct?.nameVn,
              mainProductNameEn: item.mainProduct?.nameEn,
              paidToDateBasic: checkRulePaidToDate(
                item.policyStatus as string,
                item.premCessDate as string,
                item.nextPaidPremium as number,
                item.paidToDateBasic as string
              )
                ? _.isEmpty(item.paidToDateBasic) || _.isNil(item.paidToDateBasic)
                  ? ''
                  : moment(item.paidToDateBasic, 'DD/MM/YYYY').format('YYYYMMDD')
                : '',
              mainProductCode: item.mainProduct?.code
            }))
          )
          setTaskCount(res.paging.total)
        }),
        bindLoading,
        ZIO.unsafeRun({})
      )
    }
  }

  const getDetermineContractYear = () => {
    if(dataPolicy) {
      const paidToDate = PulseOpsFormat.dateStringtoFormat(dataPolicy?.paidToDateBasic, 'YYYYMMDD')
      const riskCommDate = PulseOpsFormat.dateStringtoFormat(dataPolicy?.riskCommenceDate, 'YYYYMMDD')
      return Math.trunc((Number(paidToDate) - Number(riskCommDate))/10000) + 1
    }
    return '-'
  }

  const getPTD = () => {
    if(dataPolicy) {
      const paidToDate = PulseOpsFormat.dateStringtoFormat(dataPolicy?.paidToDateBasic, 'DD/MM/YYYY')
      const paidToDateD60 = addDays(PulseOpsFormat.stringToDate(paidToDate) ?? 0, 60)
      return !isNaN(paidToDateD60.getTime()) ? PulseOpsFormat.datetoFormat(paidToDateD60, 'DD/MM/YYYY') : '-'
    }
    return '-'
  }

  const CommonInfo = () => {
    return (
      <OBSectionContent style={{marginTop: 20}}>
        <OBSectionRow style={IBStyles.row}>
          <OBSectionCol>
            <Text style={{ fontWeight: '700', color: '#58647A', marginTop: 5, marginBottom: 4, fontSize: 15 }}>
              {t('Outbound:OBSpecialInfo:DetermineContractYear')}
            </Text>
            <Text style={{ fontSize: 15, marginTop: 4 }}>{getDetermineContractYear()}</Text>
          </OBSectionCol>
          <OBSectionCol>
            <Text style={{ fontWeight: '700', color: '#58647A', marginTop: 5, marginBottom: 4, fontSize: 15 }}>
              {t('Outbound:OBSpecialInfo:PTDD60')}
            </Text>
            <Text style={{ fontSize: 15, marginTop: 4 }}>{getPTD()}</Text>
          </OBSectionCol>
        </OBSectionRow>
      </OBSectionContent>
    )
  }

  const ContractList = () => {
    return (
      <OBSectionContent style={{marginVertical: 20}}>
        <OBTitle textStyle={{}} text={t('Outbound:OBSpecialInfo:POContractList')}></OBTitle>
        <OBSectionRow style={[IBStyles.row, { marginVertical: 20 }]}>
          <OBSectionCol>
            <Select
              label={t('Outbound:OBSpecialInfo:StatusGroup')}
              options={listStatusGroup}
              onChange={(value) => value && setItemsFilter({...itemsFilter, statusGroup:value })}
              value={itemsFilter.statusGroup}
            />
          </OBSectionCol>
          <OBSectionCol>
            <Select
              label={t('Outbound:OBSpecialInfo:Role')}
              options={listRole}
              onChange={(value) => value && setItemsFilter({...itemsFilter, role:value })}
              value={itemsFilter.role}
            />
          </OBSectionCol>
        </OBSectionRow>
        <IBGeneralTable
          dataTable={dataTable}
          data={dataFilter}
          loading={loading}
          paging={{
            page: page,
            pageSize: pageSize,
            taskCount: taskCount,
            setPage: setPage,
            setPageSize: setPageSize
          }}
          paginationStyle={{ marginRight: 60 }}
        />
      </OBSectionContent>
    )
  }

  return (
    <View>
      {CommonInfo()}
      {ContractList()}
    </View>
  )
}